<template>
  <div class="crud_form_wrapper single_show_content_wrapper bundle_show">
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.showBundle") }}</h4>
    </div>

    <div class="col-12 text-end mb-3">
      <v-btn @click="$router.go(-1)" style="color: #ef3405">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>

    <div v-if="loading" class="text-center py-5">
      <v-progress-circular indeterminate color="primary" size="50" />
    </div>

    <div v-else class="single_step_form_content_wrapper">
      <div class="page_header">
        <div class="page_header__meta">
          <v-chip large :color="statusColor" text-color="white">
            <i class="fal fa-box-open mr-1"></i>
            {{ data.status_label }}
          </v-chip>
        </div>
        <span class="page_header__number">
          <i class="fal fa-hashtag"></i>
          {{ data.bundle_number }}
        </span>
      </div>

      <bundle-summary-stats :data="data" />

      <bundle-people-section
        :data="data"
        :can-assign-driver="canAssignDriver"
        @assign-driver="assignDriverDialog = true"
      />

      <bundle-actions-bar
        :can-reassign-driver="canReassignDriver"
        :is-bundle-finished="isBundleFinished"
        @assign-driver="assignDriverDialog = true"
        @view-invoice="viewBundleInvoice"
        @download-invoice="downloadBundleInvoice"
      />

      <bundle-packages-section
        :packages="data.packages"
        @show-qr="showQrModal"
        @show-details="showPackageDetails"
        @view-invoice="viewPackageInvoice"
      />

      <div class="created_at_footer">
        <i class="fal fa-calendar-alt"></i>
        {{ $t("PLACEHOLDERS.created_at") }}: {{ data.created_at }}
      </div>
    </div>

    <bundle-qr-dialog
      v-model="qrModal"
      :qr="selectedQr"
      :package-number="selectedPackageNumber"
    />

    <bundle-assign-driver-dialog
      v-model="assignDriverDialog"
      :drivers="drivers"
      :selected-driver.sync="selectedDriver"
      :drivers-loading="driversLoading"
      :loading="assigningDriver"
      @driver-search="onDriverSearch"
      @driver-open="onDriverOpen"
      @confirm="confirmAssignDriver"
    />

    <bundle-invoice-dialog
      v-model="invoiceDialog"
      :loading="invoiceLoading"
      :invoice-data="invoiceData"
      @download="downloadInvoicePDF"
    />

    <bundle-package-details-dialog
      v-model="packageDetailsDialog"
      :pkg="selectedPackage"
    />
  </div>
</template>

<script>
import BundleSummaryStats from "./components/BundleSummaryStats.vue";
import BundlePeopleSection from "./components/BundlePeopleSection.vue";
import BundleActionsBar from "./components/BundleActionsBar.vue";
import BundlePackagesSection from "./components/BundlePackagesSection.vue";
import BundleQrDialog from "./components/BundleQrDialog.vue";
import BundleAssignDriverDialog from "./components/BundleAssignDriverDialog.vue";
import BundleInvoiceDialog from "./components/BundleInvoiceDialog.vue";
import BundlePackageDetailsDialog from "./components/BundlePackageDetailsDialog.vue";
import {
  getBundleStatusColor,
  getFilenameFromDisposition,
  normalizeInvoiceData,
} from "./utils/bundleHelpers";

export default {
  name: "ShowBundle",

  components: {
    BundleSummaryStats,
    BundlePeopleSection,
    BundleActionsBar,
    BundlePackagesSection,
    BundleQrDialog,
    BundleAssignDriverDialog,
    BundleInvoiceDialog,
    BundlePackageDetailsDialog,
  },

  data() {
    return {
      loading: false,
      qrModal: false,
      selectedQr: null,
      selectedPackageNumber: null,
      assignDriverDialog: false,
      assigningDriver: false,
      selectedDriver: null,
      drivers: [],
      driversPage: 1,
      driversLastPage: 1,
      driversSearch: "",
      driversLoading: false,
      driversLoadingMore: false,
      driversHasMore: false,
      invoiceDialog: false,
      invoiceLoading: false,
      invoiceData: null,
      currentInvoiceUrl: null,
      packageDetailsDialog: false,
      selectedPackage: null,
      data: {
        id: null,
        bundle_number: null,
        status: null,
        status_label: null,
        packages_count: 0,
        delivered_count: null,
        returned_count: null,
        weight: null,
        total_weight: null,
        reward: 0,
        delivery_prize: 0,
        service_type: null,
        distance_km: null,
        duration_mins: null,
        driver: null,
        provider: null,
        packages: [],
        max_package_dims: null,
        min_package_dims: null,
        delivery_point_count: 0,
        created_at: null,
      },
    };
  },

  computed: {
    statusColor() {
      return getBundleStatusColor(this.data.status);
    },

    canAssignDriver() {
      return !this.data.driver;
    },

    canReassignDriver() {
      return (
        !!this.data.driver &&
        ["new", "searching_driver", "pending", "assigned"].includes(
          this.data.status
        )
      );
    },

    isBundleFinished() {
      return ["مكتمل", "Completed"].includes(this.data.status_label);
    },
  },

  methods: {
    showQrModal(qr, packageNumber) {
      this.selectedQr = qr;
      this.selectedPackageNumber = packageNumber;
      this.qrModal = true;
    },

    showPackageDetails(pkg) {
      this.selectedPackage = pkg;
      this.packageDetailsDialog = true;
    },

    async downloadPdfFile(url, fallbackName) {
      const res = await this.$axios.get(url, {
        responseType: "blob",
        params: { format: "pdf" },
        headers: {
          Accept: "application/pdf",
        },
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute(
        "download",
        getFilenameFromDisposition(
          res.headers?.["content-disposition"],
          fallbackName
        )
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
    },

    async viewPackageInvoice(pkg) {
      this.invoiceDialog = true;
      this.invoiceLoading = true;
      this.currentInvoiceUrl = `bundles/packages/${pkg.id}/invoice`;
      try {
        const res = await this.$axios.get(`bundles/packages/${pkg.id}/invoice`);
        this.invoiceData = normalizeInvoiceData(res.data.data || res.data);
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.invoiceLoading = false;
      }
    },

    async viewBundleInvoice() {
      this.invoiceDialog = true;
      this.invoiceLoading = true;
      this.currentInvoiceUrl = `bundles/${this.data.id}/invoice`;
      try {
        const res = await this.$axios.get(`bundles/${this.data.id}/invoice`);
        this.invoiceData = normalizeInvoiceData(res.data.data || res.data);
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.invoiceLoading = false;
      }
    },

    async downloadInvoicePDF() {
      if (!this.currentInvoiceUrl) return;
      try {
        await this.downloadPdfFile(
          this.currentInvoiceUrl,
          `invoice_${Date.now()}.pdf`
        );
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },

    async downloadBundleInvoice() {
      try {
        await this.downloadPdfFile(
          `bundles/${this.data.id}/invoice`,
          `bundle_invoice_${this.data.bundle_number}.pdf`
        );
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },

    async getDrivers(reset = false) {
      if (reset) {
        this.driversPage = 1;
        this.drivers = [];
        this.driversHasMore = false;
      }

      const isFirstPage = this.driversPage === 1;
      if (isFirstPage) {
        this.driversLoading = true;
      } else {
        this.driversLoadingMore = true;
      }

      try {
        const res = await this.$axios({
          method: "GET",
          url: "drivers",
          params: {
            per_page: 10,
            is_approved_by_admin: 1,
            page: this.driversPage,
            search: this.driversSearch || undefined,
          },
        });

        const items = res.data.data.map((item) => ({
          id: item.id,
          name: item.name,
          value: item.id,
        }));

        this.drivers = reset ? items : [...this.drivers, ...items];
        this.driversLastPage = res.data.meta?.last_page ?? 1;
        this.driversHasMore = this.driversPage < this.driversLastPage;
      } catch (error) {
        console.log(error);
      } finally {
        this.driversLoading = false;
        this.driversLoadingMore = false;
      }
    },

    onDriverSearch(query) {
      this.driversSearch = query;
      clearTimeout(this._driverSearchTimer);
      this._driverSearchTimer = setTimeout(() => {
        this.getDrivers(true).then(() => this.attachDriverScroll());
      }, 350);
    },

    onDriverOpen() {
      if (this.drivers.length === 0) {
        this.getDrivers(true).then(() => this.attachDriverScroll());
      } else {
        this.$nextTick(() => this.attachDriverScroll());
      }
    },

    attachDriverScroll() {
      if (this._driverScrollEl) {
        this._driverScrollEl.removeEventListener(
          "scroll",
          this._driverScrollHandler
        );
        this._driverScrollEl = null;
      }

      if (!this.driversHasMore) return;

      this.$nextTick(() => {
        const root =
          document.querySelector(".bundle_assign_dialog") || this.$el;
        const wrapper = root.querySelector(
          ".driver-select .multiselect__content-wrapper"
        );
        if (!wrapper) return;

        this._driverScrollEl = wrapper;
        this._driverScrollHandler = () => {
          const { scrollTop, scrollHeight, clientHeight } = wrapper;
          if (
            scrollHeight - scrollTop - clientHeight < 50 &&
            this.driversHasMore &&
            !this.driversLoadingMore
          ) {
            this.driversPage++;
            this.getDrivers().then(() => {
              if (!this.driversHasMore && this._driverScrollEl) {
                this._driverScrollEl.removeEventListener(
                  "scroll",
                  this._driverScrollHandler
                );
                this._driverScrollEl = null;
              }
            });
          }
        };
        wrapper.addEventListener("scroll", this._driverScrollHandler);
      });
    },

    async confirmAssignDriver() {
      if (!this.selectedDriver) return;

      this.assigningDriver = true;
      try {
        await this.$axios.post(`bundles/${this.data.id}/assign-driver`, {
          driver_id: this.selectedDriver.id,
        });
        this.$message.success(this.$t("MESSAGES.driver_assigned_success"));
        this.assignDriverDialog = false;
        this.selectedDriver = null;
        this.getBundleData();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.assigningDriver = false;
      }
    },

    async getBundleData() {
      this.loading = true;
      try {
        const res = await this.$axios.get(`bundles/${this.$route.params.id}`);
        const bundle = res.data.data || res.data;

        this.data = {
          id: bundle.id,
          bundle_number: bundle.bundle_number,
          status: bundle.status,
          status_label: bundle.status_label,
          packages_count: bundle.packages_count,
          delivered_count: bundle.delivered_count,
          returned_count: bundle.returned_count,
          weight: bundle.weight,
          total_weight: bundle.total_weight,
          reward: bundle.reward,
          delivery_prize: bundle.delivery_prize,
          service_type: bundle.service_type,
          distance_km: bundle.distance_km || bundle.total_expected_distance,
          duration_mins: bundle.duration_mins,
          driver: bundle.driver,
          provider: bundle.provider,
          packages: bundle.packages || [],
          max_package_dims: bundle.max_package_dims,
          min_package_dims: bundle.min_package_dims,
          delivery_point_count: bundle.delivery_point_count,
          created_at: bundle.created_at,
        };
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.getBundleData();
  },

  beforeDestroy() {
    if (this._driverScrollEl) {
      this._driverScrollEl.removeEventListener(
        "scroll",
        this._driverScrollHandler
      );
    }
  },
};
</script>

<style lang="scss">
@import "./components/bundleShow.scss";
</style>
